import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { ArrowLeft, Table2, Plus, Search, MoreHorizontal, Edit, Trash2, Download, FileText, FileSpreadsheet, FileCode, FileType, FileJson } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TablaTVC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/catastro/tabla-tvc`, { headers });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      item.tabla?.toLowerCase().includes(s) ||
      item.tipologia?.toLowerCase().includes(s) ||
      item.descripcion?.toLowerCase().includes(s)
    );
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/catastro/tabla-tvc`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/catastro/tabla-tvc/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/api/catastro/tabla-tvc/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  const exportToExcel = () => { const ed = data.map(i => ({ 'Tabla': i.tabla, 'Tipología': i.tipologia, 'Descripción': i.descripcion, 'Valor A': i.valor_a, 'Valor B': i.valor_b, 'Valor C': i.valor_c, 'Valor D': i.valor_d, 'Valor E': i.valor_e, 'Valor F': i.valor_f })); const ws = XLSX.utils.json_to_sheet(ed); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Tabla TVC'); XLSX.writeFile(wb, `tabla_valores_construccion_${new Date().toISOString().split('T')[0]}.xlsx`); };
  const exportToPDF = () => { const doc = new jsPDF('l', 'mm', 'a4'); doc.setFontSize(16); doc.text('Tabla de Valores de la Construcción', 14, 15); doc.setFontSize(10); doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-VE')}`, 14, 22); autoTable(doc, { head: [['Tabla', 'Tipología', 'Descripción', 'A', 'B', 'C', 'D', 'E', 'F']], body: data.map(i => [i.tabla, i.tipologia, i.descripcion || '-', i.valor_a?.toFixed(2) || '-', i.valor_b?.toFixed(2) || '-', i.valor_c?.toFixed(2) || '-', i.valor_d?.toFixed(2) || '-', i.valor_e?.toFixed(2) || '-', i.valor_f?.toFixed(2) || '-']), startY: 28, styles: { fontSize: 8 }, headStyles: { fillColor: [234, 179, 8] } }); doc.save(`tabla_valores_construccion_${new Date().toISOString().split('T')[0]}.pdf`); };
  const exportToCSV = () => { const h = ['Tabla', 'Tipología', 'Descripción', 'Valor A', 'Valor B', 'Valor C', 'Valor D', 'Valor E', 'Valor F']; let csv = h.join(',') + '\n'; data.forEach(i => { csv += [`"${(i.tabla||'').replace(/"/g,'""')}"`,`"${(i.tipologia||'').replace(/"/g,'""')}"`,`"${(i.descripcion||'').replace(/"/g,'""')}"`,i.valor_a||'',i.valor_b||'',i.valor_c||'',i.valor_d||'',i.valor_e||'',i.valor_f||''].join(',') + '\n'; }); const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' }); saveAs(blob, `tabla_valores_construccion_${new Date().toISOString().split('T')[0]}.csv`); };
  const exportToText = () => { let text = 'TABLA DE VALORES DE LA CONSTRUCCIÓN\n'; text += `Fecha de generación: ${new Date().toLocaleDateString('es-VE')}\n`; text += '='.repeat(100) + '\n\n'; data.forEach((i, idx) => { text += `Registro #${idx + 1}\n` + '-'.repeat(50) + '\n'; text += `Tabla: ${i.tabla}\nTipología: ${i.tipologia || '-'}\nDescripción: ${i.descripcion || '-'}\n`; ['A','B','C','D','E','F'].forEach(l => text += `Valor ${l}: ${i[`valor_${l.toLowerCase()}`]?.toFixed(2) || '-'}\n`); text += '\n'; }); const blob = new Blob([text], { type: 'text/plain;charset=utf-8' }); saveAs(blob, `tabla_valores_construccion_${new Date().toISOString().split('T')[0]}.txt`); };
  const exportToHTML = () => { const h = ['Tabla', 'Tipología', 'Descripción', 'Valor A', 'Valor B', 'Valor C', 'Valor D', 'Valor E', 'Valor F']; let html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Tabla de Valores de la Construcción</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#ca8a04}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background-color:#eab308;color:white}tr:nth-child(even){background-color:#f2f2f2}</style></head><body><h1>Tabla de Valores de la Construcción</h1><p>Fecha de generación: ${new Date().toLocaleDateString('es-VE')}</p><table><thead><tr>${h.map(c=>`<th>${c}</th>`).join('')}</tr></thead><tbody>`; data.forEach(i => { html += `<tr><td>${i.tabla}</td><td>${i.tipologia||'-'}</td><td>${i.descripcion||'-'}</td>`; ['a','b','c','d','e','f'].forEach(l => html += `<td>${i[`valor_${l}`]?.toFixed(2)||'-'}</td>`); html += '</tr>'; }); html += '</tbody></table></body></html>'; const blob = new Blob([html], { type: 'text/html;charset=utf-8' }); saveAs(blob, `tabla_valores_construccion_${new Date().toISOString().split('T')[0]}.html`); };
  const exportToJSON = () => { const ed = data.map(i => ({ tabla: i.tabla, tipologia: i.tipologia, descripcion: i.descripcion, valor_a: i.valor_a, valor_b: i.valor_b, valor_c: i.valor_c, valor_d: i.valor_d, valor_e: i.valor_e, valor_f: i.valor_f })); const json = JSON.stringify(ed, null, 2); const blob = new Blob([json], { type: 'application/json;charset=utf-8' }); saveAs(blob, `tabla_valores_construccion_${new Date().toISOString().split('T')[0]}.json`); };

  return (
    <div className="space-y-6" data-testid="catastro-submodulo">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catastro')} className="hover:bg-slate-100"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center"><Table2 className="w-6 h-6 text-yellow-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tabla TVC</h1>
          <p className="text-slate-500">Gestión de tabla tvc</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredData.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{data.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <Popover open={exportOpen} onOpenChange={setExportOpen}>
            <PopoverTrigger asChild><Button variant="outline" data-testid="btn-exportar"><Download className="w-4 h-4 mr-2" />Exportar</Button></PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="end">
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { exportToExcel(); setExportOpen(false); }} data-testid="btn-export-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { exportToPDF(); setExportOpen(false); }} data-testid="btn-export-pdf"><FileText className="w-4 h-4 mr-2" />PDF</Button>
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { exportToCSV(); setExportOpen(false); }} data-testid="btn-export-csv"><FileType className="w-4 h-4 mr-2" />CSV</Button>
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { exportToText(); setExportOpen(false); }} data-testid="btn-export-text"><FileCode className="w-4 h-4 mr-2" />Texto</Button>
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { exportToHTML(); setExportOpen(false); }} data-testid="btn-export-html"><FileCode className="w-4 h-4 mr-2" />HTML</Button>
              <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => { exportToJSON(); setExportOpen(false); }} data-testid="btn-export-json"><FileJson className="w-4 h-4 mr-2" />JSON</Button>
            </PopoverContent>
          </Popover>
          <Button onClick={() => { setEditingItem(null); setFormData({}); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo"><Plus className="w-4 h-4 mr-2" />Nuevo Registro</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tabla</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tipología</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Descripción</th>
                  {['A','B','C','D','E','F'].map(l => <th key={l} className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">{l}</th>)}
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-400">No hay registros</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.tabla}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.tipologia}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.descripcion || "-"}</td>
                      {['valor_a','valor_b','valor_c','valor_d','valor_e','valor_f'].map(k => <td key={k} className="px-4 py-3 text-sm text-slate-600 text-right">{item[k]?.toFixed(2) || "-"}</td>)}
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditingItem(item); setFormData(item); setDialogOpen(true); }}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingItem ? "Editar Tabla TVC" : "Nuevo Registro - Tabla TVC"}</DialogTitle></DialogHeader>
          <div className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Tabla</Label><Input value={formData.tabla || ""} onChange={(e) => setFormData({...formData, tabla: e.target.value})} placeholder="Tabla" data-testid="form-tabla" /></div>
              <div className="space-y-2"><Label>Tipología</Label><Input value={formData.tipologia || ""} onChange={(e) => setFormData({...formData, tipologia: e.target.value})} placeholder="Tipología" data-testid="form-tipologia" /></div>
              <div className="space-y-2"><Label>Descripción</Label><Input value={formData.descripcion || ""} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} placeholder="Descripción" data-testid="form-descripcion-tvc" /></div>
              {['A','B','C','D','E','F'].map(l => (
                <div key={l} className="space-y-2">
                  <Label>{l}</Label>
                  <Input type="number" step="0.01" value={formData[`valor_${l.toLowerCase()}`] || ""} onChange={(e) => setFormData({...formData, [`valor_${l.toLowerCase()}`]: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" data-testid={`form-valor-${l.toLowerCase()}`} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablaTVC;
